/**
 * Помощь: что сделать, если VPN не работает.
 *
 * Сначала — состояние аккаунта (закончилась подписка, упёрлись в лимит
 * устройств или трафика): это самые частые причины, и их видно сразу.
 * Дальше — шаги по порядку, внизу — поддержка, если ничего не помогло.
 */
import { useNavigate } from 'react-router';

import { useSubscription } from '@/entities/subscription/queries';
import type { Subscription } from '@/shared/api/schemas';
import { config } from '@/shared/config/env';
import { formatShortDate, formatTraffic } from '@/shared/lib/format';
import { isGuest } from '@/shared/lib/guest';
import { openExternal } from '@/shared/lib/links';
import { useBackButton } from '@/shared/lib/useBackButton';
import { Screen } from '@/shared/ui/Screen';
import { Button } from '@/shared/ui/kit/Button';

import styles from './Help.module.css';

/** Глобальная версия Happ: в российском App Store её нет. */
const HAPP_APP_STORE = 'https://apps.apple.com/app/happ-proxy-utility/id6504287215';

function Status({ data, onPlans }: { data: Subscription; onPlans: () => void }) {
  const accessible = data.items.filter((item) => item.has_access);
  const counts = accessible.map((item) => item.devices_used);
  const used = counts.every((n) => typeof n === 'number') ? counts.reduce<number>((sum, n) => sum + (n ?? 0), 0) : null;
  const limit = data.device_limit_total;
  const devicesFull = used !== null && limit !== null && used >= limit;
  const trafficOver = data.traffic_limit_bytes !== null && data.traffic_used_bytes >= data.traffic_limit_bytes;
  const until = accessible[0]?.expires_at ?? null;

  if (!data.has_access) {
    return (
      <div className={`${styles.status} ${styles.bad}`}>
        <b>Подписка не активна.</b> Поэтому VPN и не подключается — продлите её, и всё заработает по той же ссылке.
        <Button block size="s" onClick={onPlans}>
          Продлить
        </Button>
      </div>
    );
  }
  return (
    <div className={`${styles.status} ${devicesFull || trafficOver ? styles.bad : ''}`}>
      <div className={styles.line}>
        <span>Подписка</span>
        <span>{until ? `активна до ${formatShortDate(until)}` : 'активна'}</span>
      </div>
      <div className={styles.line}>
        <span>Устройства</span>
        <span>
          {used === null ? '—' : used} / {limit === null ? '∞' : limit}
        </span>
      </div>
      <div className={styles.line}>
        <span>Трафик</span>
        <span>{formatTraffic(data.traffic_used_bytes, data.traffic_limit_bytes)}</span>
      </div>
      {devicesFull ? (
        <p className={styles.warn}>
          Подключено максимум устройств — новое не подключится. Удалите лишнее в «Мои подписки» или выберите тариф с
          большим числом устройств.
        </p>
      ) : null}
      {trafficOver ? <p className={styles.warn}>Трафик закончился — докупите гигабайты или продлите тариф.</p> : null}
    </div>
  );
}

export function HelpPage() {
  const navigate = useNavigate();
  const guest = isGuest();
  const { data } = useSubscription();
  useBackButton(() => void navigate(-1));

  return (
    <Screen isLoading={false} error={null}>
      <h1 className={styles.head}>VPN не работает?</h1>
      <p className={styles.intro}>Пройдите шаги по порядку — обычно хватает первых двух.</p>

      {!guest && data ? <Status data={data} onPlans={() => void navigate('/plans')} /> : null}

      <ol className={styles.steps}>
        <li>
          <b>Переподключитесь.</b> Выключите VPN в Happ и включите снова. Не помогло — включите и выключите режим
          полёта.
        </li>
        <li>
          <b>Обновите подписку в Happ.</b> Потяните список серверов вниз или нажмите ⟳ у подписки — подтянутся свежие
          настройки.
        </li>
        <li>
          <b>Смените сервер.</b> Выберите «Авто» — он сам найдёт самый быстрый. Или попробуйте другую страну.
        </li>
        <li>
          <b>Мобильный интернет режут?</b> Включите сервер «Белый Список» — он работает, даже когда остальное
          заблокировано.
        </li>
        <li>
          <b>«Белый Список» не работает на iPhone?</b> Нужна последняя версия Happ, а в российском App Store её нет.
          Смените регион Apple ID на другую страну и установите{' '}
          <a
            className={styles.link}
            href={HAPP_APP_STORE}
            onClick={(event) => {
              event.preventDefault();
              openExternal(HAPP_APP_STORE);
            }}
          >
            Happ из App Store
          </a>
          .
        </li>
        <li>
          <b>Добавьте подписку заново.</b> Удалите её в Happ и снова нажмите «Добавить в Happ» в «Мои подписки».
        </li>
      </ol>

      <div className={styles.support}>
        <p className={styles.intro}>Ничего не помогло? Напишите нам — разберёмся.</p>
        <Button block onClick={() => openExternal(config.supportUrl)}>
          Написать в поддержку
        </Button>
        {!guest ? (
          <Button block variant="quiet" onClick={() => void navigate('/subscriptions')}>
            Мои подписки
          </Button>
        ) : null}
      </div>
    </Screen>
  );
}
